import { useState, useCallback, useMemo, Fragment } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { listMonitors, listMonitorRuns, getProbe, getMonitorUptime, getMonitorCache, getMonitorIncidents, getMonitorLatency, updateMonitorAlerts, UpdateMonitorAlertsParams, LatencyBucket } from "../lib/api";
import LatencyChart from "../components/LatencyChart";
import UptimeBar, { DayInfo } from "../components/UptimeBar";
import ResultsTable from "../components/ResultsTable";
import RoutingInsight from "../components/RoutingInsight";
import CertPanel from "../components/CertPanel";
import CachePanel from "../components/CachePanel";
import { Monitor, MonitorRun, MonitorIncident, ProbeRun, REGIONS } from "../types";
import { usePlan } from "../App";
import { Bone } from "../components/Skeleton";

const CURATED_TIMEZONES: { value: string; label: string }[] = [
  { value: "UTC",                             label: "UTC – Universal" },
  { value: "Pacific/Honolulu",                label: "HST – Hawaii" },
  { value: "America/Anchorage",               label: "AKST – Alaska" },
  { value: "America/Los_Angeles",             label: "PST – North America West" },
  { value: "America/Denver",                  label: "MST – North America Mountain" },
  { value: "America/Chicago",                 label: "CST – North America Central" },
  { value: "America/New_York",                label: "EST – North America East" },
  { value: "America/Halifax",                 label: "AST – Atlantic" },
  { value: "America/St_Johns",                label: "NST – Newfoundland" },
  { value: "America/Sao_Paulo",               label: "BRT – South America" },
  { value: "America/Argentina/Buenos_Aires",  label: "ART – Argentina" },
  { value: "Europe/London",                   label: "GMT/BST – United Kingdom" },
  { value: "Europe/Paris",                    label: "CET – Central Europe" },
  { value: "Europe/Helsinki",                 label: "EET – Eastern Europe" },
  { value: "Europe/Moscow",                   label: "MSK – Russia/Moscow" },
  { value: "Asia/Dubai",                      label: "GST – Gulf" },
  { value: "Asia/Kolkata",                    label: "IST – India" },
  { value: "Asia/Bangkok",                    label: "ICT – Southeast Asia" },
  { value: "Asia/Singapore",                  label: "SGT – Singapore / Malaysia" },
  { value: "Asia/Shanghai",                   label: "CST – China" },
  { value: "Asia/Tokyo",                      label: "JST – Japan" },
  { value: "Asia/Seoul",                      label: "KST – Korea" },
  { value: "Australia/Sydney",                label: "AEST – Australia East" },
  { value: "Pacific/Auckland",                label: "NZST – New Zealand" },
];

function statusLabel(s: string): string {
  if (s === "complete") return "Successful";
  if (s === "error") return "Failed";
  if (s === "running") return "Running";
  return s;
}

function statusColor(s: string): string {
  if (s === "complete") return "#39D98A";
  if (s === "error") return "#FF5C5C";
  return "#FDDD48";
}

function statusBg(s: string): string {
  if (s === "complete") return "rgba(57,217,138,0.1)";
  if (s === "error") return "rgba(255,92,92,0.1)";
  return "rgba(253,221,72,0.1)";
}

function LockedPanel({ title, description }: { title: string; description: string }) {
  return (
    <div className="glass" style={{ padding: 32, marginBottom: 16, textAlign: "center" }}>
      <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#EFF2F5", fontSize: 15 }}>{title}</p>
      <p style={{ margin: "0 0 20px", color: "#98A1AB", fontSize: 13 }}>{description}</p>
      <Link to="/upgrade" className="btn-primary" style={{ padding: "10px 24px", fontSize: 14, display: "inline-block", textDecoration: "none" }}>
        Upgrade to Pro
      </Link>
    </div>
  );
}

function MonitorSettingsPanel({ monitor, onClose }: { monitor: Monitor; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [latencyMs, setLatencyMs] = useState(monitor.alert_latency_ms != null ? String(monitor.alert_latency_ms) : "");
  const [email, setEmail] = useState(monitor.alert_email ?? "");
  const [webhookUrl, setWebhookUrl] = useState(monitor.alert_webhook_url ?? "");
  const [certExpiryDays, setCertExpiryDays] = useState(monitor.alert_cert_expiry_days != null ? String(monitor.alert_cert_expiry_days) : "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const params: UpdateMonitorAlertsParams = {
        alert_latency_ms: latencyMs ? parseInt(latencyMs, 10) : null,
        alert_email: email || null,
        alert_webhook_url: webhookUrl || null,
        alert_cert_expiry_days: certExpiryDays ? parseInt(certExpiryDays, 10) : null,
      };
      await updateMonitorAlerts(monitor.id, params);
      queryClient.invalidateQueries({ queryKey: ["monitors"] });
      setSaveSuccess(true);
    } catch {
      setSaveError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  const fieldLabel = (text: string) => (
    <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#EFF2F5", marginBottom: 4 }}>{text}</span>
  );
  const fieldHint = (text: string) => (
    <p style={{ margin: "5px 0 0", fontSize: 11, color: "#98A1AB" }}>{text}</p>
  );

  return (
    <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>Monitor Settings</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", color: "#98A1AB", cursor: "pointer", fontSize: 18, padding: 0, lineHeight: 1 }}>✕</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          {fieldLabel("High Latency Threshold")}
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#98A1AB" }}>
            Runs with P95 above this value are flagged as high latency and trigger alerts.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" min={1} value={latencyMs} onChange={(e) => setLatencyMs(e.target.value)}
              placeholder="e.g. 1000" className="input-dark"
              style={{ width: 100, fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
            <span style={{ fontSize: 13, color: "#98A1AB" }}>ms</span>
          </div>
          {fieldHint("Leave blank to disable high-latency detection.")}
        </div>

        <div>
          {fieldLabel("Email Notifications")}
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#98A1AB" }}>
            Receive an email when an error or high-latency incident is detected.
          </p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" className="input-dark"
            style={{ width: "100%", maxWidth: 320, fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
          {fieldHint("Leave blank to disable email alerts.")}
        </div>

        <div>
          {fieldLabel("Webhook URL")}
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#98A1AB" }}>
            POST a JSON payload to this URL whenever an incident occurs.
          </p>
          <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://…" className="input-dark"
            style={{ width: "100%", maxWidth: 400, fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
          {fieldHint("Leave blank to disable webhook alerts.")}
        </div>

        {monitor.method !== "TCP" && (
          <div>
            {fieldLabel("TLS Certificate Expiry Alert")}
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#98A1AB" }}>
              Alert when the TLS certificate expires within this many days.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="number" min={1} max={365} value={certExpiryDays} onChange={(e) => setCertExpiryDays(e.target.value)}
                placeholder="e.g. 14" className="input-dark"
                style={{ width: 80, fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
              <span style={{ fontSize: 13, color: "#98A1AB" }}>days</span>
            </div>
            {fieldHint("Leave blank to disable cert expiry alerts.")}
          </div>
        )}

        <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.04)", borderRadius: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#98A1AB" }}>SMS notifications are not currently available.</p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={handleSave} disabled={saving} className="btn-primary"
            style={{ fontSize: 13, padding: "7px 20px", fontFamily: "inherit", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
          {saveSuccess && <span style={{ fontSize: 12, color: "#39D98A" }}>Settings saved</span>}
          {saveError && <span style={{ fontSize: 12, color: "#FF5C5C" }}>{saveError}</span>}
        </div>
      </div>
    </div>
  );
}

type IncidentKind = "all" | "error" | "latency";

function QueryPanel({ monitor, timezone }: { monitor: Monitor; timezone: string }) {
  const today = new Date().toISOString().slice(0, 10);
  const minDate = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);

  const [queryDate, setQueryDate] = useState(today);
  const [queryTime, setQueryTime] = useState("00:00");
  const [queryDuration, setQueryDuration] = useState(24);
  const [appliedParams, setAppliedParams] = useState<{ since: string; until: string; label: string } | null>(null);
  const [queryError, setQueryError] = useState<string | null>(null);

  const [kind, setKind] = useState<IncidentKind>("all");
  const [thresholdInput, setThresholdInput] = useState(String(monitor.alert_latency_ms ?? 1000));
  const [appliedThreshold, setAppliedThreshold] = useState(monitor.alert_latency_ms ?? 1000);

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runDetails, setRunDetails] = useState<Record<string, ProbeRun>>({});
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);

  const { data: latencyData, isLoading: latencyLoading } = useQuery({
    queryKey: ["qpanel-latency", monitor.id, appliedParams?.since, appliedParams?.until],
    queryFn: () => getMonitorLatency(monitor.id, "custom", appliedParams!.since, appliedParams!.until),
    enabled: !!appliedParams,
  });

  const { data: incidentData, isLoading: incidentLoading, isError: incidentError } = useQuery({
    queryKey: ["qpanel-incidents", monitor.id, appliedParams?.since, appliedParams?.until, kind, appliedThreshold],
    queryFn: () => getMonitorIncidents(monitor.id, {
      type: kind,
      since: appliedParams!.since,
      until: appliedParams!.until,
      threshold_ms: appliedThreshold,
      limit: 200,
    }),
    enabled: !!appliedParams,
  });

  function runQuery() {
    setQueryError(null);
    const dt = new Date(`${queryDate}T${queryTime}`);
    if (isNaN(dt.getTime())) { setQueryError("Invalid date or time"); return; }
    if (dt < new Date(Date.now() - 30 * 24 * 3600 * 1000)) { setQueryError("Start must be within the last 30 days"); return; }
    const now = new Date();
    if (dt >= now) { setQueryError("Start time must be in the past"); return; }
    const untilDt = new Date(Math.min(dt.getTime() + queryDuration * 3600 * 1000, now.getTime()));
    const fmtDate = new Intl.DateTimeFormat(undefined, { timeZone: timezone, month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const fmtTime = new Intl.DateTimeFormat(undefined, { timeZone: timezone, hour: "2-digit", minute: "2-digit" });
    setAppliedParams({ since: dt.toISOString(), until: untilDt.toISOString(), label: `${fmtDate.format(dt)} – ${fmtTime.format(untilDt)}` });
    setExpandedRunId(null);
  }

  function applyThreshold() {
    const n = parseInt(thresholdInput, 10);
    if (!isNaN(n) && n > 0) setAppliedThreshold(n);
  }

  async function handleRowClick(inc: MonitorIncident) {
    if (expandedRunId === inc.run_id) { setExpandedRunId(null); return; }
    setExpandedRunId(inc.run_id);
    if (!runDetails[inc.run_id]) {
      setLoadingRunId(inc.run_id);
      try {
        const detail = await getProbe(inc.run_id);
        setRunDetails((prev) => ({ ...prev, [inc.run_id]: detail }));
      } finally { setLoadingRunId(null); }
    }
  }

  const incidents: MonitorIncident[] = incidentData?.incidents ?? [];
  const buckets: LatencyBucket[] = latencyData?.buckets ?? [];
  const windowMs = appliedParams
    ? new Date(appliedParams.until).getTime() - new Date(appliedParams.since).getTime()
    : 0;
  const shortWindow = windowMs <= 24 * 3600 * 1000;

  const chartTooltip = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) return null;
      const b = payload[0]?.payload as LatencyBucket;
      return (
        <div style={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3, padding: "8px 12px", fontSize: 12, color: "#EFF2F5", minWidth: 160 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{new Date(label).toLocaleString(undefined, { timeZone: timezone })}</div>
          {b.p50 != null && <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span style={{ color: "#98A1AB" }}>P50</span><span style={{ color: "#39D98A", fontWeight: 700 }}>{b.p50}ms</span></div>}
          {b.p95 != null && <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><span style={{ color: "#98A1AB" }}>P95</span><span style={{ color: "#FDDD48" }}>{b.p95}ms</span></div>}
          {b.error_count > 0 && <div style={{ color: "#FF5C5C", marginTop: 2 }}>{b.error_count} error{b.error_count > 1 ? "s" : ""}</div>}
          <div style={{ color: "#98A1AB" }}>{b.total_count} run{b.total_count !== 1 ? "s" : ""}</div>
        </div>
      );
    },
    [timezone]
  );

  const kindBtnStyle = (k: IncidentKind): React.CSSProperties => ({
    padding: "4px 12px", fontSize: 12, border: "1px solid",
    borderColor: kind === k ? (k === "error" ? "#FF5C5C" : k === "latency" ? "#FDDD48" : "#5B8DEF") : "rgba(255,255,255,0.1)",
    background: kind === k ? (k === "error" ? "rgba(255,92,92,0.1)" : k === "latency" ? "rgba(253,221,72,0.1)" : "rgba(91,141,239,0.1)") : "transparent",
    color: kind === k ? (k === "error" ? "#FF5C5C" : k === "latency" ? "#FDDD48" : "#5B8DEF") : "#98A1AB",
    borderRadius: 3, cursor: "pointer", fontWeight: kind === k ? 700 : 400, fontFamily: "inherit",
  });

  return (
    <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>Query</h3>

      <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap", marginBottom: queryError ? 8 : 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: 11, color: "#98A1AB", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Date</span>
          <input type="date" value={queryDate} min={minDate} max={today} onChange={(e) => setQueryDate(e.target.value)}
            className="input-dark" style={{ fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: 11, color: "#98A1AB", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Start time</span>
          <input type="time" value={queryTime} onChange={(e) => setQueryTime(e.target.value)}
            className="input-dark" style={{ fontSize: 13, padding: "5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <span style={{ fontSize: 11, color: "#98A1AB", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Window</span>
          <select value={queryDuration} onChange={(e) => setQueryDuration(Number(e.target.value))} className="input-dark"
            style={{ fontSize: 13, padding: "5px 28px 5px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212", cursor: "pointer", appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2020/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(152,161,171,0.7)' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
            {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
        </div>
        <button onClick={runQuery} className="btn-primary" style={{ fontSize: 13, padding: "6px 20px", fontFamily: "inherit" }}>
          Run Query
        </button>
      </div>

      {queryError && <p style={{ margin: "0 0 16px", fontSize: 12, color: "#FF5C5C" }}>{queryError}</p>}

      {!appliedParams && !queryError && (
        <p style={{ color: "#98A1AB", fontSize: 13, margin: 0 }}>
          Pick a date, start time, and window length — then click Run Query to search for incidents.
        </p>
      )}

      {appliedParams && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <p style={{ margin: 0, fontSize: 12, color: "#98A1AB" }}>{appliedParams.label}</p>
            <button onClick={() => setAppliedParams(null)} title="Clear results"
              style={{ background: "none", border: "none", color: "#98A1AB", cursor: "pointer", fontSize: 16, padding: "0 0 0 12px", lineHeight: 1, flexShrink: 0 }}>
              ✕
            </button>
          </div>

          {latencyLoading ? (
            <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: "#98A1AB", fontSize: 13, marginBottom: 16 }}>Loading chart…</div>
          ) : buckets.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={buckets} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="time"
                    tickFormatter={(t) => shortWindow
                      ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: timezone })
                      : new Date(t).toLocaleDateString([], { month: "short", day: "numeric", timeZone: timezone })}
                    tick={{ fill: "#98A1AB", fontSize: 10 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis unit="ms" tick={{ fill: "#98A1AB", fontSize: 10 }} width={48} axisLine={false} tickLine={false} />
                  <Tooltip content={chartTooltip} />
                  <Line type="basis" dataKey="p50" stroke="#39D98A" strokeWidth={2} connectNulls
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    dot={(props: any) => {
                      const b = props.payload as LatencyBucket;
                      if (b?.error_count > 0) return <circle key={props.key} cx={props.cx} cy={props.cy} r={4} fill="#FF5C5C" stroke="#121212" strokeWidth={2} />;
                      return <g key={props.key} />;
                    }}
                    activeDot={{ r: 4, fill: "#39D98A", stroke: "#121212", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#98A1AB" }}>
                  <div style={{ width: 16, height: 2, background: "#39D98A", borderRadius: 2 }} /> Median (P50)
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#98A1AB" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF5C5C" }} /> Error
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: "#98A1AB", fontSize: 13, marginBottom: 16 }}>No latency data for this window</div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14, flexWrap: "wrap" }}>
            <button style={kindBtnStyle("all")} onClick={() => { setKind("all"); setExpandedRunId(null); }}>All</button>
            <button style={kindBtnStyle("error")} onClick={() => { setKind("error"); setExpandedRunId(null); }}>Errors</button>
            <button style={kindBtnStyle("latency")} onClick={() => { setKind("latency"); setExpandedRunId(null); }}>High Latency</button>
            {(kind === "latency" || kind === "all") && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
                <span style={{ fontSize: 12, color: "#98A1AB" }}>Threshold</span>
                <input type="number" min={1} value={thresholdInput}
                  onChange={(e) => setThresholdInput(e.target.value)}
                  onBlur={applyThreshold} onKeyDown={(e) => e.key === "Enter" && applyThreshold()}
                  className="input-dark" style={{ width: 72, fontSize: 12, padding: "3px 8px", borderRadius: 3, color: "#EFF2F5", background: "#121212" }} />
                <span style={{ fontSize: 12, color: "#98A1AB" }}>ms</span>
              </div>
            )}
          </div>

          {incidentLoading ? (
            <div style={{ color: "#98A1AB", fontSize: 13, padding: "16px 0", textAlign: "center" }}>Loading…</div>
          ) : incidentError ? (
            <p style={{ color: "#FF5C5C", fontSize: 13, margin: 0 }}>Failed to load incidents. Please try again.</p>
          ) : incidents.length === 0 ? (
            <p style={{ color: "#98A1AB", fontSize: 14, margin: 0 }}>
              {kind === "error" ? "No errors in this window." : kind === "latency" ? `No runs exceeded ${appliedThreshold}ms P95.` : "No incidents in this window."}
            </p>
          ) : (
            <>
              <p style={{ margin: "0 0 10px", fontSize: 12, color: "#98A1AB" }}>
                {incidents.length} incident{incidents.length !== 1 ? "s" : ""}{incidentData?.has_more ? "+" : ""} · click a row for region breakdown
              </p>
              <table className="tbl-hover" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    {(["left", "left", "right", "right", "right", "right"] as const).map((align, i) => (
                      <th key={i} style={{ textAlign: align, padding: "8px 12px", color: "#98A1AB", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {["Time", "Type", "P50", "P95", "Errors", "Regions"][i]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incidents.map((inc) => {
                    const isExpanded = expandedRunId === inc.run_id;
                    const detail = runDetails[inc.run_id];
                    const isLoadingRow = loadingRunId === inc.run_id;
                    return (
                      <Fragment key={inc.run_id}>
                        <tr onClick={() => handleRowClick(inc)}
                          style={{ borderBottom: isExpanded ? "none" : "1px solid rgba(255,255,255,0.05)", cursor: "pointer", background: isExpanded ? "rgba(255,255,255,0.03)" : "transparent", transition: "background 0.15s" }}>
                          <td style={{ padding: "10px 12px", color: "#EFF2F5" }}>{new Date(inc.created_at).toLocaleString(undefined, { timeZone: timezone })}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", gap: 4 }}>
                              {inc.is_error && <span style={{ fontSize: 11, fontWeight: 700, color: "#FF5C5C", background: "rgba(255,92,92,0.1)", padding: "2px 7px", borderRadius: 3 }}>Error</span>}
                              {inc.is_high_latency && <span style={{ fontSize: 11, fontWeight: 700, color: "#FDDD48", background: "rgba(253,221,72,0.1)", padding: "2px 7px", borderRadius: 3 }}>High Latency</span>}
                            </div>
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: inc.p50 != null ? "#39D98A" : "#98A1AB", fontWeight: inc.p50 != null ? 700 : 400 }}>{inc.p50 != null ? `${inc.p50}ms` : "—"}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: inc.p95 != null ? "#FDDD48" : "#98A1AB" }}>{inc.p95 != null ? `${inc.p95}ms` : "—"}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: inc.error_count > 0 ? "#FF5C5C" : "#98A1AB" }}>{inc.error_count > 0 ? inc.error_count : "—"}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", color: "#98A1AB" }}>{inc.regions_done}/{inc.region_count}</td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                            <td colSpan={6} style={{ padding: "0 12px 16px" }}>
                              {isLoadingRow ? (
                                <div style={{ color: "#98A1AB", fontSize: 13, padding: "12px 0" }}>Loading region data…</div>
                              ) : detail ? (
                                <div style={{ marginTop: 8, padding: 16, background: "rgba(18,18,18,0.5)", borderRadius: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
                                  <ResultsTable results={detail.results ?? []} regionCount={detail.region_count} regionsDone={detail.regions_done} selectedRegions={monitor.regions} />
                                  <RoutingInsight results={detail.results ?? []} />
                                  <CachePanel results={detail.results ?? []} />
                                  <CertPanel results={detail.results ?? []} />
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function MonitorDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const fromGroup = (location.state as { fromGroup?: { id: string; name: string } } | null)?.fromGroup;
  const plan = usePlan();
  const isPro = plan === "pro";
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [runDetails, setRunDetails] = useState<Record<string, ProbeRun>>({});
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const systemTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const [timezone, setTimezone] = useState<string>(() => localStorage.getItem("worldrtt-timezone") || systemTz);

  const handleTimezoneChange = useCallback((tz: string) => {
    setTimezone(tz);
    localStorage.setItem("worldrtt-timezone", tz);
  }, []);

  const { data: monitorsData, isLoading: monitorsLoading, isError: monitorsError } = useQuery({ queryKey: ["monitors"], queryFn: listMonitors });
  const { data: runsData, isError: runsError } = useQuery({
    queryKey: ["monitor-runs", id],
    queryFn: () => listMonitorRuns(id!, 200),
    enabled: !!id,
    refetchInterval: 60_000,
  });
  const { data: uptimeData } = useQuery({
    queryKey: ["monitor-uptime", id, timezone],
    queryFn: () => getMonitorUptime(id!, timezone),
    enabled: !!id && isPro,
    refetchInterval: 5 * 60_000,
  });

  const { data: cacheData } = useQuery({
    queryKey: ["monitor-cache", id],
    queryFn: () => getMonitorCache(id!, "24h"),
    enabled: !!id && isPro,
    refetchInterval: 5 * 60_000,
  });

  const monitor: Monitor | undefined = monitorsData?.monitors?.find((m: Monitor) => m.id === id);
  const runs: MonitorRun[] = runsData?.runs ?? [];

  const latestCompleteRun = runs.find((r) => r.status === "complete");
  const { data: latestRunDetail } = useQuery({
    queryKey: ["probe-detail", latestCompleteRun?.id ?? "none"],
    queryFn: () => getProbe(latestCompleteRun!.id),
    enabled: !!latestCompleteRun?.id,
    staleTime: 5 * 60_000,
  });

  const handleRunClick = useCallback(async (run: MonitorRun) => {
    if (expandedRunId === run.id) { setExpandedRunId(null); return; }
    setExpandedRunId(run.id);
    if (!runDetails[run.id]) {
      setLoadingRunId(run.id);
      try {
        const detail = await getProbe(run.id);
        setRunDetails((prev) => ({ ...prev, [run.id]: detail }));
      } finally {
        setLoadingRunId(null);
      }
    }
  }, [expandedRunId, runDetails]);

  if (monitorsError) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#98A1AB", padding: "0 24px" }}>
          <p style={{ fontSize: 16, color: "#EFF2F5", marginBottom: 8 }}>Service temporarily unavailable</p>
          <p style={{ fontSize: 14, marginBottom: 20 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()} className="btn-glass" style={{ padding: "8px 20px", fontSize: 13 }}>
            Refresh
          </button>
        </div>
      </div>
    );
  }

  if (monitorsLoading || !monitor) {
    return (
      <div style={{ minHeight: "100vh" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
          <div style={{ marginBottom: 28 }}>
            <Bone w={220} h={24} style={{ marginBottom: 8 }} />
            <Bone w={320} h={14} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Bone w={64} h={12} />
            <Bone w={180} h={30} />
          </div>
          <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
            <Bone w="100%" h={48} />
          </div>
          <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
            <Bone w={140} h={15} style={{ marginBottom: 16 }} />
            <Bone w="100%" h={140} />
          </div>
          <div className="glass" style={{ padding: 24 }}>
            <Bone w={120} h={15} style={{ marginBottom: 6 }} />
            <Bone w={260} h={12} style={{ marginBottom: 18 }} />
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <Bone w={140} h={13} />
                <Bone w={64} h={20} />
                <Bone w={52} h={13} />
                <Bone w={44} h={13} />
                <Bone w={36} h={13} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const days90: DayInfo[] = uptimeData?.days.map((d) => ({
    date: d.date, status: d.status, upCount: d.up_count, downCount: d.down_count,
  })) ?? [];
  const uptimePct = uptimeData?.uptime_pct ?? 100;

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
        {fromGroup && (
          <Link
            to={`/dashboard/monitor-groups/${fromGroup.id}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#98A1AB", textDecoration: "none", marginBottom: 20 }}
          >
            ← {fromGroup.name}
          </Link>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: settingsOpen ? 12 : 28 }}>
          <div>
            <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 700, color: "#EFF2F5" }}>{monitor.name}</h1>
            <p style={{ margin: 0, color: "#98A1AB", fontSize: 14 }}>
              {monitor.url.replace(/^tcp:\/\//, "")} · {monitor.method} · every {monitor.interval_minutes}m
            </p>
          </div>
          {isPro && (
            <button onClick={() => setSettingsOpen((v) => !v)} title="Monitor settings"
              style={{ background: "none", border: "1px solid", borderColor: settingsOpen ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)", borderRadius: 4, color: settingsOpen ? "#EFF2F5" : "#98A1AB", cursor: "pointer", fontSize: 15, padding: "5px 8px", marginTop: 4, lineHeight: 1 }}>
              ⚙
            </button>
          )}
        </div>

        {isPro && settingsOpen && (
          <MonitorSettingsPanel monitor={monitor} onClose={() => setSettingsOpen(false)} />
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "#98A1AB", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Timezone
          </span>
          <select
            value={timezone}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="input-dark"
            style={{
              width: "auto", background: "#121212", borderRadius: 3,
              color: "#EFF2F5", fontSize: 13, padding: "4px 28px 4px 10px",
              cursor: "pointer", outline: "none", appearance: "none", WebkitAppearance: "none",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='rgba(152,161,171,0.7)' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center",
            }}
          >
            {(() => {
              const systemEntry = CURATED_TIMEZONES.find((t) => t.value === systemTz);
              const systemLabel = systemEntry ? `${systemEntry.label} (System)` : `${systemTz} (System)`;
              return (
                <>
                  <option value={systemTz}>{systemLabel}</option>
                  <optgroup label="Timezones">
                    {CURATED_TIMEZONES.filter((t) => t.value !== systemTz).map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                </>
              );
            })()}
          </select>
        </div>

        {isPro ? (
          <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
            <UptimeBar days={days90} uptimePct={uptimePct} timezone={timezone} />
          </div>
        ) : (
          <LockedPanel title="90-Day Uptime History" description="See daily uptime status across all regions over the last 90 days." />
        )}

        {isPro ? (
          <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
            <LatencyChart monitorId={id!} title="Latency History" timezone={timezone} latencyThreshold={monitor.alert_latency_ms} />
          </div>
        ) : (
          <LockedPanel title="Latency Charts" description="Track P50/P95/P99 latency trends over 1h, 24h, 7d, and 30d." />
        )}

        {isPro ? (
          <QueryPanel monitor={monitor} timezone={timezone} />
        ) : (
          <LockedPanel title="Query" description="Search for errors and high-latency incidents within any time window over the last 30 days." />
        )}

        {isPro && cacheData && monitor.method !== "TCP" && (
          <div className="glass" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                CDN Cache — last 24h
              </span>
              {cacheData.provider !== "none" && (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#98A1AB", background: "rgba(255,255,255,0.06)", padding: "2px 8px", borderRadius: 3 }}>
                  {cacheData.provider.charAt(0).toUpperCase() + cacheData.provider.slice(1)}
                </span>
              )}
            </div>
            {Object.keys(cacheData.regions).length === 0 ? (
              <p style={{ color: "#98A1AB", fontSize: 13, margin: 0 }}>No CDN cache headers detected in the last 24h.</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    {["Region", "Hit Rate", "Hits", "Misses", "Bypass", "Other"].map((label, i) => (
                      <th key={label} style={{ padding: "6px 10px", textAlign: i === 0 ? "left" : "right", fontSize: 11, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(cacheData.regions).sort(([a], [b]) => a.localeCompare(b)).map(([region, stats]) => (
                    <tr key={region} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                      <td style={{ padding: "9px 10px", color: "#EFF2F5", fontWeight: 500 }}>
                        {(REGIONS as Record<string, string>)[region] ?? region}
                      </td>
                      <td style={{ padding: "9px 10px", textAlign: "right", fontWeight: 700, color: stats.hit_rate >= 0.9 ? "#39D98A" : stats.hit_rate >= 0.7 ? "#FDDD48" : "#FF5C5C" }}>
                        {(stats.hit_rate * 100).toFixed(1)}%
                      </td>
                      <td style={{ padding: "9px 10px", textAlign: "right", color: "#39D98A" }}>{stats.hit}</td>
                      <td style={{ padding: "9px 10px", textAlign: "right", color: "#FDDD48" }}>{stats.miss}</td>
                      <td style={{ padding: "9px 10px", textAlign: "right", color: "#FF8C00" }}>{stats.bypass}</td>
                      <td style={{ padding: "9px 10px", textAlign: "right", color: "#98A1AB" }}>{stats.other}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {(() => {
          const latestResults = latestRunDetail?.results ?? [];
          const hasCertInfo = latestResults.some((r) => r.cert_info != null);
          const hasRoutingInfo = latestResults.filter((r) => r.resolved_ip && !r.error).length >= 2;
          if (!latestRunDetail || (!hasCertInfo && !hasRoutingInfo)) return null;
          return (
            <div className="glass" style={{ padding: "20px 24px", marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Network Insights
              </span>
              <RoutingInsight results={latestResults} />
              <CertPanel results={latestResults} />
            </div>
          );
        })()}

        <div className="glass" style={{ padding: 24 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 600, color: "#EFF2F5" }}>Recent Runs</h3>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#98A1AB" }}>
            Click any row to see per-region latency breakdown
          </p>
          {runsError ? (
            <div style={{ color: "#98A1AB", padding: "16px 0" }}>
              <p style={{ fontSize: 14, color: "#EFF2F5", margin: "0 0 4px" }}>Service temporarily unavailable</p>
              <p style={{ fontSize: 13, margin: 0 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
            </div>
          ) : runs.length === 0 ? (
            <p style={{ color: "#98A1AB", fontSize: 14 }}>No runs yet — check back after the next interval.</p>
          ) : (
            <table className="tbl-hover" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <th style={thStyle("left")}>Started</th>
                  <th style={thStyle("right")}>Status</th>
                  <th style={thStyle("right")}>Median (P50)</th>
                  <th style={thStyle("right")}>P95</th>
                  <th style={thStyle("right")}>Regions</th>
                </tr>
              </thead>
              <tbody>
                {runs.slice(0, 15).map((r) => {
                  const isExpanded = expandedRunId === r.id;
                  const detail = runDetails[r.id];
                  const isLoading = loadingRunId === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        id={`run-${r.id}`}
                        onClick={() => handleRunClick(r)}
                        style={{ borderBottom: isExpanded ? "none" : "1px solid rgba(255,255,255,0.05)", cursor: "pointer", background: isExpanded ? "rgba(255,255,255,0.03)" : "transparent", transition: "background 0.15s" }}
                      >
                        <td style={{ padding: "10px 12px", color: "#EFF2F5" }}>
                          {new Date(r.created_at).toLocaleString(undefined, { timeZone: timezone })}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          <span style={{ color: statusColor(r.status), fontWeight: 600, fontSize: 12, padding: "2px 8px", borderRadius: 3, background: statusBg(r.status) }}>
                            {statusLabel(r.status)}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: r.p50 != null ? "#39D98A" : "#98A1AB", fontWeight: r.p50 != null ? 700 : 400 }}>
                          {r.p50 != null ? `${r.p50}ms` : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: r.p95 != null ? "#FDDD48" : "#98A1AB" }}>
                          {r.p95 != null ? `${r.p95}ms` : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#98A1AB" }}>
                          {r.regions_done}/{r.region_count}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                          <td colSpan={5} style={{ padding: "0 12px 16px" }}>
                            {isLoading ? (
                              <div style={{ color: "#98A1AB", fontSize: 13, padding: "12px 0" }}>Loading region data…</div>
                            ) : detail ? (
                              <div style={{ marginTop: 8, padding: 16, background: "rgba(18,18,18,0.5)", borderRadius: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
                                <ResultsTable results={detail.results ?? []} regionCount={detail.region_count} regionsDone={detail.regions_done} selectedRegions={monitor.regions} />
                                <RoutingInsight results={detail.results ?? []} />
                                <CachePanel results={detail.results ?? []} />
                                <CertPanel results={detail.results ?? []} />
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function thStyle(align: "left" | "right"): React.CSSProperties {
  return { textAlign: align, padding: "8px 12px", color: "#98A1AB", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" };
}
